import { createContext, createContextHook } from '@/utils';
import type { QueryError } from '@/utils/errors';
import type { Category, CompleteCategory } from '../../types';

type MutationCallbacks = {
  onSuccess?: () => void;
  onError?: (error: QueryError) => void;
};

type UpdateCallbacks = {
  onSuccess?: () => void;
  onError?: (
    error: QueryError,
    variables: unknown,
    context?: { prevCategory?: Category }
  ) => void;
};

/**
 * Supplies the experience's category list and CRUD operations to the category
 * select components (CategoryProvider is the sole provider). Colors resolve
 * from each category's colorName through the experience palette directly in
 * the components — no color data flows through this context.
 */
export type CategoryData = {
  categories: Category[];
  createCategory: (
    category: CompleteCategory,
    options?: MutationCallbacks
  ) => void;
  isCreatePending: boolean;
  updateCategory: (
    category: Pick<Category, 'id' | 'text' | 'colorName'>,
    options?: UpdateCallbacks
  ) => void;
  removeCategory: (categoryId: string, options?: MutationCallbacks) => void;
  isRemovePending: boolean;
  updateSortOrder: (categories: Category[]) => void;
};

export const CategoryDataContext = createContext<CategoryData | undefined>(
  undefined,
  'CategoryDataContext'
);

export const useCategoryData = createContextHook(CategoryDataContext);
